// GEL BÊTA le 07/10/2026. Voir docs/GEL_BETA.md.
// Architecture figée : paiement dual, Coco via settings, rôles user_roles/has_role, âge 18 soft, pas de Connect.
// Porte ouverte : corrections de retours testeurs uniquement sur ordre Hiti, lot unique, sans rouvrir ces règles.
// Interdit sans Hiti : tama-wip→main, édition client.ts / types.ts / .env / config.toml / previewAuthStorage.
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { isAudioUnlocked, onAudioUnlocked } from "@/services/playback/audioSession";
import { applyImageFit, watchVideoFit } from "@/services/playback/videoFit";
import { adPlaybackUrl, logAdEvent, type ServedAd } from "@/services/ads";

const ProductPurchase = lazy(() => import("@/components/ProductPurchase"));
const ReportDialog = lazy(() => import("@/components/ReportDialog").then((m) => ({ default: m.ReportDialog })));

interface AdBreakProps {
  /** Publicité servie par la régie. Absente : rien n'est affiché. */
  ad?: ServedAd | null;
  onComplete: () => void;
  isActive?: boolean;
  /** Remonte l'état du chrono au feed : la pub verrouille le swipe tant que canSkip est faux. */
  onCanSkipChange?: (canSkip: boolean) => void;
  /** Aperçu administrateur : aucun comptage, aucun bouton « Continuer ». */
  preview?: boolean;
}

const PRODUCT_SELECT =
  "id, user_id, title, description, price, type, is_active, thumbnail_url, is_tama_catalog, service_type, service_delivery_method, service_duration, availability, profiles:user_id ( id, full_name, username, avatar_url )";

/**
 * Carte publicitaire plein écran. Son propre élément <video>, jamais deckEngine.
 * Le compte à rebours s'arrête dès que la carte n'est plus réellement vue
 * (arrière-plan, fiche produit ou signalement ouverts).
 */
const AdBreak = ({ ad, onComplete, isActive = true, onCanSkipChange, preview = false }: AdBreakProps) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const duration = ad?.durationSeconds ?? 0;
  const [timeLeft, setTimeLeft] = useState(duration);
  const [canSkip, setCanSkip] = useState(false);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState === "visible");
  const [product, setProduct] = useState<any>(null);
  const [productOpen, setProductOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [muted, setMuted] = useState(() => !isAudioUnlocked());
  const impressionSent = useRef(false);
  const completionSent = useRef(false);
  const failed = useRef(false);

  const running = !!ad && isActive && pageVisible && !productOpen && !reportOpen && !canSkip;
  const shouldPlay = !!ad && isActive && pageVisible && !productOpen && !reportOpen;

  useEffect(() => {
    onCanSkipChange?.(canSkip || !ad);
  }, [canSkip, ad, onCanSkipChange]);

  useEffect(() => {
    const onVisibility = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  // Le son suit la session, sans jamais être forcé.
  useEffect(() => {
    if (isAudioUnlocked()) return;
    return onAudioUnlocked(() => setMuted(false));
  }, []);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          setCanSkip(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  // Affichage compté une seule fois, carte active ET document visible.
  useEffect(() => {
    if (!ad || preview || failed.current || impressionSent.current) return;
    if (isActive && pageVisible) {
      impressionSent.current = true;
      logAdEvent(ad, "impression");
    }
  }, [ad, isActive, pageVisible, preview]);

  useEffect(() => {
    if (!ad || preview || !canSkip || completionSent.current || failed.current) return;
    completionSent.current = true;
    logAdEvent(ad, "completion");
  }, [ad, canSkip, preview]);

  // Cadrage : même règle des 65 % que les vidéos du fil (effet séparé de la lecture).
  useEffect(() => {
    if (ad?.mediaType === "video") {
      if (videoRef.current) watchVideoFit(videoRef.current);
      return;
    }
    const img = imageRef.current;
    if (!img) return;
    const update = () => applyImageFit(img);
    const observer = new ResizeObserver(update);
    observer.observe(img);
    img.addEventListener("load", update);
    update();
    return () => {
      observer.disconnect();
      img.removeEventListener("load", update);
    };
  }, [ad?.mediaType, ad?.mediaUrl]);

  const activeRef = useRef(isActive);
  activeRef.current = isActive;

  // GELÉ le 02/10/2026. Même amorçage que deckEngine au commit 97034709. Ne pas réécrire.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || ad?.mediaType !== "video") return;
    if (isActive) {
      video.muted = muted;
      video.play().catch(() => undefined);
      return;
    }
    video.muted = true;
    const attempt = video.play();
    const stop = () => {
      if (activeRef.current) return;
      video.pause();
    };
    if (attempt && typeof attempt.then === "function") {
      attempt.then(stop).catch(() => undefined);
    } else {
      stop();
    }
  }, [isActive, muted, ad?.mediaUrl, ad?.mediaType]);

  // Pause à part (l'effet gelé n'est pas touché) : fiche, signalement ou onglet caché
  // coupent la vidéo et le son ; la reprise suit l'état de son déjà calculé.
  const wasPausedRef = useRef(false);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || ad?.mediaType !== "video") return;
    if (!shouldPlay) {
      wasPausedRef.current = true;
      video.pause();
      return;
    }
    if (wasPausedRef.current && isActive) {
      wasPausedRef.current = false;
      video.muted = muted;
      video.play().catch(() => undefined);
    }
  }, [shouldPlay, isActive, muted, ad?.mediaType]);

  /** Création indisponible : on saute l'emplacement sans rien compter. */
  const handleMediaError = () => {
    if (failed.current || !ad) return;
    failed.current = true;
    console.error("Création publicitaire indisponible, emplacement sauté:", ad.campaignId);
    setCanSkip(true);
    if (!preview) window.setTimeout(onComplete, 0);
  };

  useEffect(() => {
    if (ad && new Date(ad.mediaExpiresAt).getTime() <= Date.now()) handleMediaError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad?.slotId]);

  const handleCta = async () => {
    if (!ad) return;
    if (!preview) logAdEvent(ad, "click");
    if (ad.ctaKind === "profile" && ad.ctaProfileUsername) {
      navigate(`/profile/@${encodeURIComponent(ad.ctaProfileUsername)}`);
    } else if (ad.ctaKind === "external" && ad.ctaUrl) {
      window.open(ad.ctaUrl, "_blank", "noopener,noreferrer");
    } else if (ad.ctaKind === "product" && ad.ctaProductId) {
      if (!product) {
        const { data, error } = await supabase
          .from("products")
          .select(PRODUCT_SELECT)
          .eq("id", ad.ctaProductId)
          .eq("is_active", true)
          .maybeSingle();
        if (error || !data) {
          console.error("Fiche produit publicitaire introuvable:", error);
          return;
        }
        setProduct(data);
      }
      setProductOpen(true);
    }
  };

  if (!ad) return null;

  return (
    <div className="relative h-full w-full overflow-hidden bg-player-background">
      {ad.mediaType === "video" ? (
        <video
          ref={videoRef}
          src={adPlaybackUrl(ad)}
          className="absolute inset-0 h-full w-full object-cover"
          playsInline
          loop
          muted
          preload="auto"
          onError={handleMediaError}
        />
      ) : (
        <img
          ref={imageRef}
          src={ad.mediaUrl}
          alt={ad.headline ?? ad.advertiserName}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          onError={handleMediaError}
        />
      )}

      {/* Mention obligatoire, toujours visible. */}
      <div className="absolute left-4 top-4 z-10 rounded-md bg-player-background/70 px-3 py-1.5 lg:top-20">
        <p className="text-xs font-semibold uppercase tracking-wide text-player-foreground">Publicité</p>
        <p className="text-sm text-player-foreground">{ad.advertiserName}</p>
      </div>

      {!preview && (
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          aria-label="Signaler cette publicité"
          className="absolute right-4 top-4 z-10 inline-flex items-center gap-1 rounded-full bg-player-background/60 px-3 py-1.5 text-xs text-player-foreground lg:top-20"
        >
          <Flag className="h-3.5 w-3.5" aria-hidden="true" />
          Signaler
        </button>
      )}

      <div className="absolute inset-x-0 bottom-0 z-10 space-y-3 bg-gradient-to-t from-player-background/90 to-transparent px-4 pb-[var(--player-bottom-inset)] pt-16 lg:pb-8">
        {ad.headline && <p className="text-lg font-semibold text-player-foreground">{ad.headline}</p>}
        <div className="flex flex-wrap items-center gap-3">
          {ad.ctaKind !== "none" && ad.ctaLabel && (
            <Button onClick={handleCta}>{ad.ctaLabel}</Button>
          )}
          {!canSkip ? (
            <span className="rounded-full bg-player-background/70 px-4 py-2 text-sm font-medium text-player-foreground">
              Ignorer dans {timeLeft}s
            </span>
          ) : (
            !preview && (
              <Button variant="secondary" onClick={onComplete}>
                Continuer
              </Button>
            )
          )}
        </div>
      </div>

      {productOpen && product && (
        <Suspense fallback={null}>
          <ProductPurchase product={product} open={productOpen} onClose={() => setProductOpen(false)} />
        </Suspense>
      )}
      {reportOpen && (
        <Suspense fallback={null}>
          <ReportDialog open={reportOpen} onOpenChange={setReportOpen} contentType="ad" contentId={ad.campaignId} />
        </Suspense>
      )}
    </div>
  );
};

export default AdBreak;
